'use client'

import { useState, useCallback, useRef, useEffect, createContext, useContext } from 'react'

const ToastContext = createContext(null)

export function useToast() {
  return useContext(ToastContext)
}

/**
 * showToast(message)                                  — 預設 2.5s
 * showToast(message, 5000)                            — 既有用法：第二參數是毫秒
 * showToast(message, { duration: 6000, action: { label: '復原', onClick: fn } })
 *   — 新用法：含 undo 按鈕；按下會立刻消失並執行 onClick
 *
 * 與既有所有 caller 完全相容（既有用法多為單參數或 (msg, number)）。
 */
export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null)
  const timerRef = useRef(null)

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    setToast(null)
  }, [])

  const showToast = useCallback((message, optsOrDuration) => {
    const isNumber = typeof optsOrDuration === 'number'
    const opts = isNumber ? {} : (optsOrDuration ?? {})
    const duration = isNumber ? optsOrDuration : (opts.duration ?? (opts.action ? 6000 : 2500))
    const action = opts.action ?? null

    if (timerRef.current) clearTimeout(timerRef.current)
    setToast({ message, action })
    timerRef.current = setTimeout(() => setToast(null), duration)
  }, [])

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current) }, [])

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 max-w-[92vw]"
          style={{
            backgroundColor: 'var(--color-cardBg)',
            color: 'var(--color-text)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card-hover)',
            fontFamily: 'var(--font-body)',
            border: '1px solid var(--color-cardBorder)',
            animation: 'toastIn 0.3s ease',
          }}
        >
          <span className="text-sm">{toast.message}</span>
          {toast.action && (
            <button
              type="button"
              onClick={() => {
                try { toast.action.onClick?.() } finally { dismiss() }
              }}
              className="text-sm font-bold whitespace-nowrap min-h-[44px] px-3 -my-1 transition-opacity hover:opacity-80"
              style={{
                color: 'var(--color-accent)',
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
              }}
            >
              {toast.action.label}
            </button>
          )}
        </div>
      )}
    </ToastContext.Provider>
  )
}
